<template>
	<transition name="ui--video">
		<player
			v-show="isOpen"
			class="player-video ui ui--video"
			:player-state="playerState"
			:player-type="playerType"
			:media-loading="mediaLoading"
		>
			<video
				class="player-video__video"
				ref="video"
				:src="source"
				style="display:block"
				controlslist="nodownload nofullscreen noremoteplayback"
				preload="none"
				@webkitendfullscreen="closeAndUnload"
				aria-hidden="true"
			></video>
			<div class="player-video__controls" :class="{ 'player-video__controls--visible': showControls }">
				<ElementButton ref="play" name="play" :content="playButtonContent" @click="play" tabindex="0" i18n />
				<ElementMedia
					type="video"
					:attribution="attribution"
					:title="title"
					:description="description"
					:remainingTime="remainingTime"
					aria-hidden="true"
				/>
				<ElementProgress
					:pastTime="currentTime"
					:duration="duration"
					:progress="progress"
					:min="0.0"
					:max="Math.round(duration)"
					:value="Math.round(currentTime)"
					:unit="$t('seconds')"
					:touched="touched"
				/>
				<ElementButton name="close" @click="closeAndUnload" content="close" i18n tabindex="0" />
			</div>
		</player>
	</transition>
</template>

<script>
import Vue from 'vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import UiBase from './base.vue';
import { actionTypes, mutationTypes } from '@/types';
import { PLAYER_TYPE, PLAYER_STATE } from '@/tools/consts';
import { debounce } from '@/tools/debounce.js';
import { logger } from '@/tools/logger';
import { uiComponents } from '@/tools/viewState';

export default {
	extends: UiBase,
	data() {
		return {
			name: uiComponents.uiVideo,
			showControls: false,
			mediaLoading: true,
		};
	},

	computed: {
		...mapState({
			title: state => state.playerVideo.title,
			attribution: state => state.playerVideo.attribution,
			description: state => state.playerVideo.description,
			duration: state => state.playerVideo.duration,
			synced: state => state.playerVideo.synced,
			source: state => state.playerVideo.source,
			playerState: state => state.playerVideo.playerState,
			currentTime: state => state.playerVideo.currentTime,
			isSynced: state => state.playerVideo.isSynced,
			// open: state => state.playerVideo.open,
			// moduleIsLoaded: state => state.loadedStoreModules['playerVideo'],
		}),
		remainingTime() {
			return this.duration - this.currentTime;
		},
		progress() {
			return this.currentTime / this.duration;
		},
		playerType() {
			return this.synced ? PLAYER_TYPE.synced : PLAYER_TYPE.default;
		},
		playButtonContent() {
			return this.playerState === PLAYER_STATE.playing ? 'pause' : 'play';
		},
	},
	methods: {
		...mapMutations([
			mutationTypes.videoSetState,
			mutationTypes.videoSetCurrentTime,
			mutationTypes.unSetSource,
			mutationTypes.videoSetPlayer,
			// mutationTypes.videoSetFullScreen,
		]),
		...mapActions([actionTypes.videoToggle]),
		play() {
			logger.log('pressed Play');
			this.videoToggle();
		},
		closeAndUnload() {
			this.unSetSource();
			this.close();
		},
		touched(leftRight) {
			if (!this.isSynced) this.$refs.video.currentTime = leftRight * this.duration;
		},
	},
	watch: {
		playerState(newState) {
			// Show the controls for 2s when the state changes.
			this.showControls = true;
			setTimeout(() => {
				this.showControls = false;
			}, 2000);

			logger.log('new player State ', newState);

			if (newState === PLAYER_STATE.playing) {
				this.$nextTick(() => {
					const playPromise = this.$refs.video.play();
					if (playPromise) {
						playPromise.catch(error => {
							logger.warn('play error', error.message);
						});
					}
				});
			} else if (newState === PLAYER_STATE.paused) {
				this.$refs.video.pause();
			} else if (newState === PLAYER_STATE.stopped) {
				this.$refs.video.currentTime = 0;
				// @flako is this pause correct should it not be play ?

				this.$refs.video.pause();
			}
		},
		isOpen(isOpen) {
			if (!isOpen) {
				this.$refs.video.pause();
			} else {
				Vue.nextTick(() => {
					this.$refs.play.$el.focus();
				});
			}
		},
	},
	mounted() {
		this.$el.addEventListener('mousemove', () => {
			this.showControls = true;
		});
		this.$el.addEventListener(
			'mousemove',
			debounce(() => {
				this.showControls = false;
			}, 2000)
		);
		const UPDATE_INTERVALL = 100;
		let lastUpdate = 0;
		// called when the playervideo is initialized
		const { video } = this.$refs;
		this.videoSetPlayer(video);
		// Transform video events in state video manipulation
		if (video && !this.isSynced) {
			// check if it is not an empty video post
			video.addEventListener('timeupdate', () => {
				if (Date.now() - lastUpdate > UPDATE_INTERVALL) {
					lastUpdate = Date.now();
					this.videoSetCurrentTime({ currentTime: video.currentTime });
				}
			});
			video.addEventListener('waiting', () => {
				this.mediaLoading = true;
			});
			video.addEventListener('seeking', () => {
				this.mediaLoading = true;
			});
			video.addEventListener('canplay', () => {
				this.mediaLoading = false;
			});
			video.addEventListener('ended', () => {
				this.close();
			});
		}
	},
};
</script>
